import { useEffect, useState, useCallback, useRef } from "react";
import {
  BookOpen,
  Upload,
  Download,
  Save,
  Trash2,
  RefreshCcw,
  FileText,
  CheckCircle2,
  AlertCircle,
  Tag,
  User,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import api from "../lib/api";

function fmtBytes(n) {
  if (!n || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AgentGuideline() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pickedFile, setPickedFile] = useState(null);
  const [versionInput, setVersionInput] = useState("");
  const [descInput, setDescInput] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agent/admin/guideline/info");
      setMeta(data);
      setVersionInput(data?.version || "");
      setDescInput(data?.description || "");
    } catch (e) {
      toast.error("Failed to load guideline info");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf") && f.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("PDF must be 25 MB or smaller");
      return;
    }
    setPickedFile(f);
  };

  const handleUpload = async () => {
    if (!pickedFile) return toast.error("Choose a PDF first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pickedFile);
      if (versionInput.trim()) fd.append("version", versionInput.trim());
      if (descInput.trim()) fd.append("description", descInput.trim());
      const { data } = await api.post("/agent/admin/guideline/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMeta(data);
      setVersionInput(data.version || "");
      setDescInput(data.description || "");
      setPickedFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Guideline PDF uploaded successfully");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveLabels = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/agent/admin/guideline", {
        version: versionInput.trim() || meta?.version || "",
        description: descInput.trim() || meta?.description || "",
      });
      setMeta(data);
      toast.success("Saved label changes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/agent/admin/guideline/download", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = meta?.filename || "Maxlive88_Agent_Guidelines_BN.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
      toast.success("Downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to the default seeded PDF? Your uploaded file will be removed.")) return;
    setResetting(true);
    try {
      const { data } = await api.delete("/agent/admin/guideline");
      setMeta(data);
      setVersionInput(data?.version || "");
      setDescInput(data?.description || "");
      toast.success("Reset to default guideline");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div data-testid="admin-agent-guideline-page" className="space-y-6">
      <PageHeader
        title="Agent Operating Handbook"
        subtitle="Upload, edit and preview the guideline PDF that agents download from their panel."
        testId="admin-agent-guideline-header"
        actions={
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-guideline-refresh"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
          >
            <RefreshCcw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />

      {/* Current PDF card */}
      <div
        className="rounded-2xl border border-emerald-100 bg-white p-5 sm:p-6 shadow-sm overflow-hidden relative"
        data-testid="admin-guideline-current"
      >
        <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-emerald-50" aria-hidden />
        <div className="absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-amber-50" aria-hidden />

        <div className="relative flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 grid place-items-center text-amber-300 flex-shrink-0 shadow-md">
            <BookOpen size={26} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-gray-900 truncate" data-testid="admin-guideline-filename">
                {meta?.filename || "—"}
              </h3>
              {meta?.version && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200">
                  <Tag size={9} /> {meta.version}
                </span>
              )}
              {meta?.available ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <CheckCircle2 size={9} /> AVAILABLE
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-md bg-rose-100 text-rose-800 border border-rose-200">
                  <AlertCircle size={9} /> MISSING
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{meta?.description || "—"}</p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Size</div>
                <div className="font-mono font-semibold text-gray-800" data-testid="admin-guideline-size">
                  {fmtBytes(meta?.size || 0)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                  <Calendar size={9} /> Uploaded
                </div>
                <div className="font-semibold text-gray-800" data-testid="admin-guideline-uploaded-at">
                  {fmtDateTime(meta?.uploaded_at)}
                </div>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                  <User size={9} /> Uploaded by
                </div>
                <div className="font-semibold text-gray-800 truncate" data-testid="admin-guideline-uploaded-by">
                  {meta?.uploaded_by || "—"}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button
              onClick={handleDownload}
              disabled={!meta?.available || downloading}
              data-testid="admin-guideline-preview-btn"
              className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg text-xs font-bold bg-emerald-700 text-white hover:bg-emerald-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloading ? <RefreshCcw size={13} className="animate-spin" /> : <Download size={13} />}
              Preview / Download
            </button>
            <button
              onClick={handleReset}
              disabled={resetting}
              data-testid="admin-guideline-reset-btn"
              className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg text-xs font-bold border border-rose-200 text-rose-700 hover:bg-rose-50 transition disabled:opacity-50"
            >
              {resetting ? <RefreshCcw size={13} className="animate-spin" /> : <Trash2 size={13} />}
              Reset to Default
            </button>
          </div>
        </div>
      </div>

      {/* Edit metadata */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm" data-testid="admin-guideline-edit-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg bg-amber-50 border border-amber-200 grid place-items-center">
            <Tag size={14} className="text-amber-700" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Edit Label &amp; Description</h3>
            <p className="text-xs text-gray-500">Update version label or description without re-uploading the PDF.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-1">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Version Label</label>
            <input
              type="text"
              value={versionInput}
              onChange={(e) => setVersionInput(e.target.value)}
              placeholder="e.g. v3.0"
              data-testid="admin-guideline-version-input"
              className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none text-sm transition"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Description</label>
            <input
              type="text"
              value={descInput}
              onChange={(e) => setDescInput(e.target.value)}
              placeholder="Short description visible in the agent panel"
              data-testid="admin-guideline-desc-input"
              className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none text-sm transition"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveLabels}
            disabled={saving}
            data-testid="admin-guideline-save-labels-btn"
            className="inline-flex items-center gap-1.5 h-9 px-5 rounded-lg text-xs font-bold bg-gray-900 text-white hover:bg-gray-800 transition disabled:opacity-50"
          >
            {saving ? <RefreshCcw size={13} className="animate-spin" /> : <Save size={13} />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Upload new PDF */}
      <div className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-5 sm:p-6" data-testid="admin-guideline-upload-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg bg-emerald-600 grid place-items-center text-white shadow-sm">
            <Upload size={14} strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Upload / Replace PDF</h3>
            <p className="text-xs text-gray-500">PDF only · max 25 MB · current file will be replaced.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label
              htmlFor="guideline-file"
              className="flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-emerald-300 bg-white hover:bg-emerald-50/60 cursor-pointer transition group"
              data-testid="admin-guideline-dropzone"
            >
              <div className="h-10 w-10 rounded-full bg-emerald-100 grid place-items-center group-hover:scale-110 transition">
                <FileText size={18} className="text-emerald-700" />
              </div>
              {pickedFile ? (
                <div className="text-center">
                  <div className="text-sm font-bold text-emerald-800">{pickedFile.name}</div>
                  <div className="text-xs text-gray-500 font-mono">{fmtBytes(pickedFile.size)}</div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-sm font-semibold text-gray-700">Click to choose a PDF</div>
                  <div className="text-xs text-gray-500">or drag &amp; drop</div>
                </div>
              )}
              <input
                ref={fileRef}
                id="guideline-file"
                type="file"
                accept="application/pdf,.pdf"
                onChange={onPick}
                className="hidden"
                data-testid="admin-guideline-file-input"
              />
            </label>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Version (for upload)</label>
              <input
                type="text"
                value={versionInput}
                onChange={(e) => setVersionInput(e.target.value)}
                placeholder="e.g. v3.1"
                className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none text-sm transition"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1.5">Description (for upload)</label>
              <input
                type="text"
                value={descInput}
                onChange={(e) => setDescInput(e.target.value)}
                placeholder="Short description"
                className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none text-sm transition"
              />
            </div>
            <button
              onClick={handleUpload}
              disabled={!pickedFile || uploading}
              data-testid="admin-guideline-upload-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-bold bg-emerald-700 text-white hover:bg-emerald-800 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {uploading ? <><RefreshCcw size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload &amp; Replace</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
